import { accountService } from "api/services/account-service";
import DetailsPanel from "components/shared-ui/details-panel/details-panel";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { IAppState } from "redux/reducers";
import { formUtils, IFormField, useField } from "utils/form-utils";
import { useApi } from "utils/hooks/use-api";
import styles from "./mfa-setup-panel.module.scss";
import { FormPanel } from "components/shared-ui/form/form-panel/form-panel";
import { FormField } from "components/shared-ui/form/form-field/form-field";
import { Check, ContentCopy } from "@mui/icons-material";
import TextInput from "components/shared-ui/input/text-input/text-input";
import { validators } from "utils/validators";

const panelDescription = `MFA has been enabled by your administrator, this setup is required to access the app. 
  Scan the QR code below with your authenticator app, or enter the secret manually, then enter the one-time code generated by your authenticator to verify and enable MFA on your account.`;
export interface IMfaSetupForm {
  code?: IFormField<string>;
  qrCodeUrl: IFormField<string>;
  secret: IFormField<string>;
}

export interface IMfaSetupFormValue {
  code?: string;
  qrUrl: string;
  secret: string;
}

const mfaSetupFormDefaultValue: IMfaSetupFormValue = {
  // code: 0,
  qrUrl: "",
  secret: "",
};

const mapStateToProps = (state: IAppState) => ({
  user: state.users.currentUser,
});

export interface ISetupMfaProps extends ReturnType<typeof mapStateToProps> {
  required?: boolean;
  open: boolean;
  onClose: (recoveryCodes?: string[]) => void;
}

export const SetupMfaPanel = connect(
  mapStateToProps,
  null,
)((props: ISetupMfaProps) => {
  const [api] = useApi();

  const [copied, setCopied] = useState(false);
  const formCodeValidators = [validators.validateMFAToken];

  const form: IMfaSetupForm = {
    code: useField<string>(mfaSetupFormDefaultValue.code, formCodeValidators),
    qrCodeUrl: useField<string>(mfaSetupFormDefaultValue.qrUrl),
    secret: useField<string>(mfaSetupFormDefaultValue.secret),
  };

  const fetchMfaSetupData = async () => {
    api(accountService.getMFASetupData()).then((response) => {
      formUtils.setFormValue(form, response);
    });
  };

  const enableMfa = async () => {
    const code = form.code.value;
    const secret = form.secret.value;
    api(accountService.enableMFA({ token: code, secret })).then((response) => {
      props.onClose(response.recoveryCodes);
    });
  };

  const copySecretToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(form.secret.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy secret:", err);
    }
  };

  useEffect(() => {
    fetchMfaSetupData();
  }, []);

  return (
    <DetailsPanel
      open={props.open}
      onCloseClick={() => (props.required ? null : props.onClose())}
      title="MFA Setup"
      className={styles.panel}
      description={panelDescription}
    >
      <FormPanel
        className={styles.root}
        okButtonText="Verify & Enable"
        okButtonClick={enableMfa}
        errorMessage={formUtils.getFormError(form)}
        okButtonDisabled={!form.code.value || !!formUtils.getFormError(form)}
      >
        <div className={styles.qrCodeSection}>
          <div className={styles.qrCodeImageWrapper}>
            {form.qrCodeUrl.value && (
              <img
                src={form.qrCodeUrl.value}
                alt="QR code for MFA setup"
                className={styles.qrCodeImage}
              />
            )}
          </div>
        </div>
        <div className={styles.manualSetupSection}>
          <div className={styles.secretContainer}>
            <span>Secret: </span>
            <span className={styles.secret}>{form.secret.value}</span>
            <button
              className={styles.copyButton}
              onClick={copySecretToClipboard}
              title="Copy secret to clipboard"
              type="button"
            >
              {copied ? <Check /> : <ContentCopy />}
            </button>
          </div>
          <FormField label="One-time code" testId="mfa-code-field">
            <TextInput
              field={form.code}
              testId="mfa-token-input"
              className={styles.codeInput}
            />
          </FormField>
        </div>
      </FormPanel>
    </DetailsPanel>
  );
});
